<template>
  <div class="marketing-stock-root pa-0 pa-md-4 fill-height align-start d-flex flex-column" dark>
    <!-- ============ HEADER BANNER ============ -->
    <v-card class="mb-4 w-100 rounded-xl header-banner" flat dark>
      <div class="header-banner-glow"></div>
      <div class="pa-5 d-flex flex-column flex-sm-row justify-space-between align-start align-sm-center position-relative" style="z-index: 2">
        <div>
          <div class="caption text-uppercase font-weight-bold letter-spaced muted-text mb-1">
            <v-icon x-small color="primary" class="mr-1">mdi-archive-outline</v-icon>
            Marketing-Lager
          </div>
          <div class="display-1 font-weight-black white--text header-title">Produkte beziehen</div>
        </div>
        <v-btn v-if="isAdmin" to="/admin/marketing-stock" color="primary" rounded outlined class="mt-3 mt-sm-0 admin-nav-btn">
          <v-icon left>mdi-cog-outline</v-icon>
          Lager verwalten (Admin)
        </v-btn>
      </div>
    </v-card>

    <!-- ============ TABS NAVIGATION ============ -->
    <v-tabs v-model="activeTab" background-color="transparent" dark color="primary" class="mb-4 tabs-nav">
      <v-tab class="text-none font-weight-bold"><v-icon left>mdi-package-variant</v-icon>Produkte beziehen</v-tab>
      <v-tab class="text-none font-weight-bold"><v-icon left>mdi-history</v-icon>Meine Bezüge</v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab" class="transparent w-100 flex-grow-1 d-flex flex-column">
      <!-- TAB 1: PRODUKTE BEZIEHEN -->
      <v-tab-item>
        <div class="fill-height d-flex flex-column">
          <!-- ============ CONTROL BAR ============ -->
          <v-card flat width="100%" class="mb-4 rounded-xl control-bar" dark>
            <div class="d-flex flex-column flex-sm-row align-stretch align-sm-center pa-3 gap-3">
              <!-- Search Field -->
              <v-text-field
                v-model="searchQuery"
                placeholder="Produkt suchen (Name, Barcode oder Kategorie)..."
                prepend-inner-icon="mdi-magnify"
                solo
                flat
                dense
                hide-details
                rounded
                background-color="rgba(255,255,255,0.04)"
                class="flex-grow-1 search-field"
                color="primary"
                clearable
                @keyup.enter="handleBarcodeSearch"></v-text-field>

              <!-- Capture Actions -->
              <div class="d-flex align-center justify-start gap-2">
                <!-- Hidden Native/File Input -->
                <input type="file" ref="fileInput" accept="image/*" style="display: none" @change="onFileSelected" />

                <!-- Capture Barcode (Analog to creditCardBills) -->
                <v-btn color="primary" rounded depressed height="40" class="px-5 action-btn" :loading="scanningFile" @click="triggerCaptureFlow">
                  <v-icon left>mdi-camera-outline</v-icon>
                  Barcode fotografieren
                </v-btn>

                <!-- Live Camera Stream option -->
                <v-btn color="secondary" rounded outlined height="40" class="px-5 action-btn" @click="openLiveScanner">
                  <v-icon left>mdi-qrcode-scan</v-icon>
                  Kamera-Live-Scan
                </v-btn>
              </div>
            </div>
          </v-card>

          <!-- ============ CATEGORIES / PRODUCTS GRID ============ -->
          <div v-if="loading" class="w-100 d-flex justify-center align-center py-12">
            <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
          </div>

          <v-row v-else-if="filteredProducts.length === 0" class="ma-0 w-100 flex-grow-1 align-center justify-center py-12">
            <div class="text-center">
              <v-icon size="64" color="grey darken-2" class="mb-4">mdi-archive-alert-outline</v-icon>
              <div class="text-h6 grey--text text--lighten-1">Keine Produkte gefunden</div>
              <div class="body-2 grey--text">Versuche es mit einer anderen Suche oder wähle ein anderes Produkt.</div>
            </div>
          </v-row>

          <div v-else class="w-100 flex-grow-1 overflow-y-auto">
            <div v-for="category in categoriesList" :key="category" class="category-section mb-6">
              <div class="category-header text-h6 font-weight-bold primary--text mb-3 px-1">{{ category }}</div>
              <v-row class="ma-n2">
                <v-col v-for="product in getProductsByCategory(category)" :key="product._id" cols="12" sm="6" md="4" lg="3" class="pa-2">
                  <v-card
                    class="product-card rounded-xl fill-height d-flex flex-column justify-space-between"
                    @click="selectProductForBooking(product)">
                    <div class="pa-4 flex-grow-1">
                      <div class="d-flex justify-space-between align-start mb-2">
                        <span class="caption product-category grey--text text-uppercase">{{ product.category }}</span>
                        <!-- Stock status badge -->
                        <v-chip v-if="product.stock !== null" :color="getStockColor(product.stock)" x-small dark class="font-weight-bold">
                          Bestand: {{ product.stock }}
                        </v-chip>
                        <v-chip v-else color="grey darken-3" x-small dark class="font-weight-bold"> Unbegrenzt </v-chip>
                      </div>
                      <div class="text-subtitle-1 font-weight-bold white--text mb-2 line-clamp-2">{{ product.title }}</div>
                      <div class="caption font-mono grey--text d-flex align-center">
                        <v-icon x-small color="grey" class="mr-1">mdi-barcode</v-icon>
                        {{ product.barcode }}
                      </div>
                    </div>
                    <v-divider class="mx-4 border-glow"></v-divider>
                    <div class="pa-3 d-flex justify-end align-center card-footer">
                      <v-btn text color="primary" small class="font-weight-bold rounded-lg text-none">
                        Entnehmen
                        <v-icon right small>mdi-chevron-right</v-icon>
                      </v-btn>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </div>
          </div>
        </div>
      </v-tab-item>

      <!-- TAB 2: MEINE BEZÜGE -->
      <v-tab-item>
        <div class="fill-height d-flex flex-column">
          <v-card flat class="rounded-xl pa-4 table-card flex-grow-1 d-flex flex-column" dark>
            <div class="d-flex align-center justify-space-between mb-4">
              <v-text-field
                v-model="myOrdersSearch"
                placeholder="Meine Bezüge durchsuchen..."
                prepend-inner-icon="mdi-magnify"
                solo
                flat
                dense
                hide-details
                rounded
                background-color="rgba(255,255,255,0.04)"
                style="max-width: 320px"
                class="search-field"
                color="primary"
                clearable></v-text-field>
            </div>

            <v-data-table
              :headers="myOrdersHeaders"
              :items="filteredMyOrders"
              :loading="loadingOrders"
              :items-per-page="15"
              dark
              class="transparent-table flex-grow-1"
              no-data-text="Du hast noch keine Produkte bezogen"
              no-results-text="Keine passenden Buchungen gefunden"
              loading-text="Bezüge werden geladen...">
              <!-- Date Rendering -->
              <template v-slot:item.createdAt="{ item }">
                <span class="caption">{{ formatDateTime(item.createdAt) }}</span>
              </template>

              <!-- Product Title/Barcode -->
              <template v-slot:item.productTitle="{ item }">
                <div>
                  <span class="font-weight-medium white--text d-block">{{ item.productTitle }}</span>
                  <span class="font-mono text-xs grey--text"
                    ><v-icon x-small color="grey" class="mr-1">mdi-barcode</v-icon>{{ item.productBarcode }}</span
                  >
                </div>
              </template>

              <!-- Studio Rendering -->
              <template v-slot:item.studio="{ item }">
                <span v-if="item.studio">{{ item.studio.name }}</span>
                <span class="grey--text caption" v-else>–</span>
              </template>

              <!-- Quantity Custom Rendering -->
              <template v-slot:item.quantity="{ item }">
                <v-chip color="rgba(25, 118, 210, 0.15)" text-color="primary" small class="font-weight-bold">
                  {{ item.quantity }}
                </v-chip>
              </template>

              <!-- Comment Rendering -->
              <template v-slot:item.comment="{ item }">
                <span v-if="item.comment" class="caption text-italic grey--text text--lighten-1" :title="item.comment">
                  {{ truncateText(item.comment, 40) }}
                </span>
                <span class="grey--text caption" v-else>–</span>
              </template>

              <!-- Actions Rendering -->
              <template v-slot:item.actions="{ item }">
                <v-btn v-if="canDeleteMyOrder(item)" icon small color="error" title="Buchung stornieren" @click="confirmCancelOrder(item)">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card>
        </div>
      </v-tab-item>
    </v-tabs-items>

    <!-- ============ LIVE SCANNER DIALOG ============ -->
    <v-dialog v-model="liveScannerOpen" max-width="500" persistent class="scanner-dialog">
      <v-card color="#121212" class="rounded-xl border-glass overflow-hidden pb-4">
        <v-card-title class="headline font-weight-bold white--text d-flex justify-space-between align-center px-6 pt-5">
          <span>Kamera Live-Scan</span>
          <v-btn icon dark @click="closeLiveScanner"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="px-6 py-4">
          <div id="live-scanner-container" class="scanner-container rounded-xl overflow-hidden position-relative">
            <div id="live-qr-reader" class="live-qr-reader"></div>
            <div class="scanner-laser"></div>
          </div>
          <div class="text-center caption grey--text mt-3">Halte den Barcode mittig in den Sucherrahmen der Kamera.</div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ============ BOOKING DIALOG ============ -->
    <v-dialog v-model="bookingDialogOpen" max-width="500" class="booking-dialog">
      <v-card color="#1a1a1a" class="rounded-xl border-glass" dark>
        <v-card-title class="headline font-weight-bold white--text px-6 pt-5"> Produkt beziehen </v-card-title>
        <v-card-text class="px-6 pb-2 pt-2" v-if="bookingProduct">
          <v-card color="rgba(255,255,255,0.03)" class="pa-4 rounded-lg mb-4" flat>
            <div class="caption text-uppercase primary--text mb-1">{{ bookingProduct.category }}</div>
            <div class="text-h6 font-weight-bold white--text mb-2">{{ bookingProduct.title }}</div>
            <div class="d-flex align-center justify-space-between">
              <span class="caption grey--text">
                <v-icon x-small color="grey" class="mr-1">mdi-barcode</v-icon>
                {{ bookingProduct.barcode }}
              </span>
              <span class="caption font-weight-bold" :style="{ color: getStockTextColor(bookingProduct.stock) }">
                Lagerbestand: {{ bookingProduct.stock !== null ? bookingProduct.stock : "Unbegrenzt" }}
              </span>
            </div>
          </v-card>

          <v-form ref="bookingForm" v-model="bookingFormValid">
            <!-- Studio Dropdown with search -->
            <v-autocomplete
              v-model="bookingStudio"
              :items="studioOptions"
              item-text="name"
              item-value="_id"
              label="Studio *"
              placeholder="Studio suchen..."
              :rules="[(v) => !!v || 'Bitte Studio auswählen']"
              outlined
              rounded
              color="primary"
              class="mb-3"></v-autocomplete>

            <!-- Quantity -->
            <v-text-field
              v-model.number="bookingQuantity"
              type="number"
              label="Anzahl *"
              outlined
              rounded
              min="1"
              color="primary"
              :rules="[
                (v) => !!v || 'Bitte Anzahl eingeben',
                (v) => Number(v) > 0 || 'Menge muss grösser als 0 sein',
                (v) => Number.isInteger(Number(v)) || 'Menge muss eine ganze Zahl sein',
              ]"
              class="mb-3"></v-text-field>

            <!-- Comment -->
            <v-textarea v-model="bookingComment" label="Kommentar (optional)" outlined rounded rows="3" color="primary" auto-grow></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions class="px-6 pb-5 justify-end">
          <v-btn text rounded @click="closeBookingDialog" class="px-5">Abbrechen</v-btn>
          <v-btn color="primary" rounded depressed class="px-6" :loading="submittingBooking" :disabled="!bookingFormValid" @click="submitBooking">
            Buchen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============ CANCEL BOOKING DIALOG ============ -->
    <v-dialog v-model="cancelDialogOpen" max-width="400">
      <v-card color="#1a1a1a" class="rounded-xl border-glass" dark>
        <v-card-title class="headline font-weight-bold white--text px-6 pt-5"> Buchung stornieren? </v-card-title>
        <v-card-text class="px-6 pb-2 pt-2" v-if="orderToCancel">
          Möchtest du deine Buchung von <b>{{ orderToCancel.quantity }}x "{{ orderToCancel.productTitle }}"</b> für das Studio
          <b>{{ orderToCancel.studio ? orderToCancel.studio.name : "–" }}</b> wirklich stornieren? Der bezogene Lagerbestand wird wieder
          gutgeschrieben.
        </v-card-text>
        <v-card-actions class="px-6 pb-5 justify-end">
          <v-btn text rounded @click="closeCancelDialog" class="px-5">Abbrechen</v-btn>
          <v-btn color="error" rounded depressed class="px-6" :loading="cancelingOrder" @click="executeCancelOrder"> Stornieren </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex"
import api from "@/api"
import moment from "moment"

export default {
  name: "MarketingStockProducts",
  data() {
    return {
      activeTab: 0,
      products: [],
      loading: false,
      searchQuery: "",

      // File Scan (analogue to creditCardBills)
      scanningFile: false,

      // Live Scan
      liveScannerOpen: false,
      html5QrCodeInstance: null,

      // Booking Dialog
      bookingDialogOpen: false,
      bookingProduct: null,
      bookingStudio: null,
      bookingQuantity: 1,
      bookingComment: "",
      bookingFormValid: false,
      submittingBooking: false,

      // User's booking history (Bezüge)
      myOrders: [],
      loadingOrders: false,
      myOrdersSearch: "",
      myOrdersHeaders: [
        { text: "Zeitpunkt", value: "createdAt", sortable: true, width: "140px" },
        { text: "Produkt", value: "productTitle", sortable: true },
        { text: "Studio", value: "studio", sortable: true },
        { text: "Menge", value: "quantity", sortable: true, width: "90px" },
        { text: "Kommentar", value: "comment", sortable: false },
        { text: "Aktionen", value: "actions", sortable: false, width: "80px", align: "end" },
      ],
      cancelDialogOpen: false,
      orderToCancel: null,
      cancelingOrder: false,
    }
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    ...mapGetters("studioStore", ["getSelectedStudioId"]),

    currentUserId() {
      return this.$store.state.userStore?.user?._id
    },

    // User Role
    isAdmin() {
      // Check if user has admin permission or rights
      const rightShorts = this.$store.state.userStore?.user?.rightShorts || []
      return rightShorts.includes("d_manageMarketingStock")
    },

    studioOptions() {
      return this.storeStudios.filter((s) => s.code && s.code !== "ot" && s.code !== "alibey").map((s) => ({ _id: s._id, name: s.name }))
    },

    filteredProducts() {
      if (!this.searchQuery) return this.products
      const query = this.searchQuery.toLowerCase().trim()
      return this.products.filter((p) => {
        return (
          p.title.toLowerCase().includes(query) || p.barcode.toLowerCase().includes(query) || (p.category && p.category.toLowerCase().includes(query))
        )
      })
    },

    categoriesList() {
      const cats = this.filteredProducts.map((p) => p.category || "Allgemein")
      return [...new Set(cats)].sort()
    },

    filteredMyOrders() {
      if (!this.myOrdersSearch) return this.myOrders
      const q = this.myOrdersSearch.toLowerCase().trim()
      return this.myOrders.filter((o) => {
        return (
          o.productTitle.toLowerCase().includes(q) ||
          o.productBarcode.toLowerCase().includes(q) ||
          (o.studio?.name && o.studio.name.toLowerCase().includes(q)) ||
          (o.comment && o.comment.toLowerCase().includes(q))
        )
      })
    },
  },
  watch: {
    activeTab(newTab) {
      if (newTab === 0) {
        this.loadProducts()
      } else if (newTab === 1) {
        this.loadMyOrders()
      }
    },
  },
  mounted() {
    this.loadProducts()
    this.bookingStudio = this.getSelectedStudioId || null
  },
  beforeDestroy() {
    this.closeLiveScanner()
  },
  methods: {
    async loadProducts() {
      this.loading = true
      try {
        this.products = await api.marketingStock.fetchProducts()
      } catch (err) {
        this.$toast.error("Produkte konnten nicht geladen werden")
        console.error(err)
      } finally {
        this.loading = false
      }
    },

    getProductsByCategory(category) {
      return this.filteredProducts.filter((p) => (p.category || "Allgemein") === category)
    },

    getStockColor(stock) {
      if (stock === null) return "grey darken-2"
      if (stock <= 0) return "error"
      if (stock < 5) return "warning"
      return "success"
    },

    getStockTextColor(stock) {
      if (stock === null) return "rgba(255,255,255,0.7)"
      if (stock <= 0) return "#ff5252"
      if (stock < 5) return "#fb8c00"
      return "#4caf50"
    },

    // --- SEARCH / BARCODE SCANNED AUTO-DETECTION ---
    handleBarcodeSearch() {
      if (!this.searchQuery) return

      // Check if query is an exact match for a barcode
      const match = this.products.find((p) => p.barcode === this.searchQuery.trim())
      if (match) {
        this.selectProductForBooking(match)
        this.searchQuery = "" // Reset search query
      }
    },

    selectProductForBooking(product) {
      this.bookingProduct = product
      this.bookingQuantity = 1
      this.bookingComment = ""
      this.bookingStudio = this.getSelectedStudioId || this.bookingStudio || null
      this.bookingDialogOpen = true
    },

    closeBookingDialog() {
      this.bookingDialogOpen = false
      this.bookingProduct = null
    },

    async submitBooking() {
      if (!this.$refs.bookingForm.validate()) return

      this.submittingBooking = true
      try {
        const payload = {
          productId: this.bookingProduct._id,
          quantity: this.bookingQuantity,
          studioId: this.bookingStudio,
          comment: this.bookingComment,
        }

        await api.marketingStock.createOrder(payload)

        this.$toast.success("Buchung erfolgreich erfasst")
        this.closeBookingDialog()

        // Reload products to get latest stock level
        await this.loadProducts()
        if (this.currentUserId) {
          this.loadMyOrders()
        }
      } catch (err) {
        const errMsg = err.response?.data?.error || "Fehler beim Buchen der Entnahme"
        this.$toast.error(errMsg)
        console.error(err)
      } finally {
        this.submittingBooking = false
      }
    },

    // --- FILE CAMERA SCANNING FLOW (LIKE CREDIT CARD BILLS) ---
    triggerCaptureFlow() {
      if (this.$vuetify.breakpoint.xsOnly && window.flutter_inappwebview) {
        // iOS/Android mobile app webview capture
        window.flutter_inappwebview
          .callHandler("openCameraNative")
          .then((result) => {
            if (result && result.success) {
              const file = this.dataURLtoFile(result.base64, result.name || "barcode_capture.jpg")
              this.processBarcodeFromImage(file)
            }
          })
          .catch(() => {
            this.$refs.fileInput.click()
          })
      } else {
        // Fallback for regular web browsers
        this.$refs.fileInput.click()
      }
    },

    dataURLtoFile(dataurl, filename) {
      const arr = dataurl.split(",")
      const mime = arr[0].match(/:(.*?);/)[1]
      const bstr = atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }
      return new File([u8arr], filename, { type: mime })
    },

    onFileSelected(event) {
      const file = event.target.files[0]
      if (file) {
        this.processBarcodeFromImage(file)
      }
      // Reset input value to allow selecting same file again
      event.target.value = ""
    },

    async processBarcodeFromImage(file) {
      this.scanningFile = true
      try {
        await this.loadHtml5Qrcode()

        // Temporary scan div for scanFile processing
        const scanDiv = document.createElement("div")
        scanDiv.id = "temp-qr-reader"
        scanDiv.style.display = "none"
        document.body.appendChild(scanDiv)

        const html5QrCode = new window.Html5Qrcode("temp-qr-reader")

        try {
          const decodedText = await html5QrCode.scanFile(file, true)
          const barcode = decodedText.trim()
          this.handleScannedBarcode(barcode)
        } catch (scanErr) {
          this.$toast.error("Kein Barcode auf dem Bild erkannt. Bitte deutlicher fotografieren.")
          console.log("Scan failure from file:", scanErr)
        } finally {
          html5QrCode.clear()
          document.body.removeChild(scanDiv)
        }
      } catch (err) {
        this.$toast.error("Scanner-Bibliothek konnte nicht initialisiert werden")
        console.error(err)
      } finally {
        this.scanningFile = false
      }
    },

    // --- LIVE CAMERA SCANNING ---
    async openLiveScanner() {
      this.liveScannerOpen = true
      try {
        await this.loadHtml5Qrcode()

        // Wait for dialog DOM update to find reader div
        this.$nextTick(() => {
          this.html5QrCodeInstance = new window.Html5Qrcode("live-qr-reader")
          const config = {
            fps: 15,
            qrbox: (width, height) => {
              const minDimension = Math.min(width, height)
              return { width: minDimension * 0.75, height: minDimension * 0.45 }
            },
          }

          this.html5QrCodeInstance
            .start(
              { facingMode: "environment" },
              config,
              (decodedText) => {
                // On success
                const barcode = decodedText.trim()
                this.closeLiveScanner()
                this.handleScannedBarcode(barcode)
              },
              (errorMessage) => {
                // Verbose error logging if needed
              },
            )
            .catch((startErr) => {
              this.$toast.error("Kamera konnte nicht gestartet werden. Bitte Berechtigungen prüfen.")
              console.error("Camera start failed:", startErr)
              this.liveScannerOpen = false
            })
        })
      } catch (err) {
        this.$toast.error("Kamera konnte nicht initialisiert werden")
        console.error(err)
        this.liveScannerOpen = false
      }
    },

    closeLiveScanner() {
      this.liveScannerOpen = false
      if (this.html5QrCodeInstance) {
        try {
          this.html5QrCodeInstance.stop().then(() => {
            this.html5QrCodeInstance = null
          })
        } catch (e) {
          console.error("Error stopping live scanner:", e)
          this.html5QrCodeInstance = null
        }
      }
    },

    handleScannedBarcode(barcode) {
      const match = this.products.find((p) => p.barcode === barcode)
      if (match) {
        this.selectProductForBooking(match)
      } else {
        this.$toast.error(`Barcode '${barcode}' erkannt, aber kein passendes Produkt im Lager gefunden.`)
      }
    },

    loadHtml5Qrcode() {
      return new Promise((resolve, reject) => {
        if (window.Html5Qrcode) {
          resolve()
          return
        }
        const script = document.createElement("script")
        script.src = "https://cdnjs.cloudflare.com/ajax/libs/html5-qrcode/2.3.8/html5-qrcode.min.js"
        script.type = "text/javascript"
        script.onload = () => resolve()
        script.onerror = (err) => reject(err)
        document.head.appendChild(script)
      })
    },

    async loadMyOrders() {
      if (!this.currentUserId) return
      this.loadingOrders = true
      try {
        this.myOrders = await api.marketingStock.fetchOrders({ createdBy: this.currentUserId })
      } catch (err) {
        this.$toast.error("Meine Bezüge konnten nicht geladen werden")
        console.error(err)
      } finally {
        this.loadingOrders = false
      }
    },

    formatDateTime(d) {
      return d ? moment(d).format("DD.MM.YYYY HH:mm") : ""
    },

    truncateText(text, length) {
      if (!text) return ""
      if (text.length <= length) return text
      return text.substring(0, length) + "..."
    },

    // --- CANCELLATION METHODS ---
    canDeleteMyOrder(order) {
      if (!order || !order.createdAt) return false
      const diffMs = new Date() - new Date(order.createdAt)
      const diffHours = diffMs / (1000 * 60 * 60)
      return diffHours < 24
    },

    confirmCancelOrder(order) {
      this.orderToCancel = order
      this.cancelDialogOpen = true
    },

    closeCancelDialog() {
      this.cancelDialogOpen = false
      this.orderToCancel = null
    },

    async executeCancelOrder() {
      if (!this.orderToCancel) return

      this.cancelingOrder = true
      try {
        await api.marketingStock.deleteOrder(this.orderToCancel._id)
        this.$toast.success("Buchung erfolgreich storniert")
        this.closeCancelDialog()

        // Refresh products and orders
        await this.loadProducts()
        await this.loadMyOrders()
      } catch (err) {
        const errMsg = err.response?.data?.error || "Fehler beim Stornieren der Buchung"
        this.$toast.error(errMsg)
        console.error(err)
      } finally {
        this.cancelingOrder = false
      }
    },
  },
}
</script>

<style scoped>
.marketing-stock-root {
  background-color: #0d0d0d;
  color: #fff;
}

.header-banner {
  background: linear-gradient(135deg, #121212, #1a1a1a);
  border: 1px solid rgba(255, 255, 255, 0.05);
  position: relative;
  overflow: hidden;
}

.header-banner-glow {
  position: absolute;
  top: -50px;
  right: -50px;
  width: 250px;
  height: 250px;
  background: radial-gradient(circle, rgba(25, 118, 210, 0.15) 0%, transparent 70%);
  filter: blur(20px);
  pointer-events: none;
}

.header-title {
  letter-spacing: -0.5px;
}

.letter-spaced {
  letter-spacing: 1.5px;
}

.muted-text {
  color: rgba(255, 255, 255, 0.5);
}

.admin-nav-btn {
  text-transform: none !important;
  font-weight: 600;
  letter-spacing: 0;
  border-color: rgba(25, 118, 210, 0.5);
  transition: all 0.3s;
}

.admin-nav-btn:hover {
  background-color: rgba(25, 118, 210, 0.08);
  border-color: #1976d2;
}

.control-bar {
  background: rgba(18, 18, 18, 0.8) !important;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  position: sticky;
  top: 8px;
  z-index: 10;
}

.search-field >>> .v-input__slot {
  transition: background 0.2s;
}

.search-field:hover >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.07) !important;
}

.action-btn {
  text-transform: none !important;
  font-weight: bold;
  letter-spacing: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.action-btn:hover {
  transform: translateY(-1px);
}

.gap-2 {
  gap: 8px;
}

.gap-3 {
  gap: 12px;
}

.product-card {
  background: rgba(25, 25, 25, 0.6) !important;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.04);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.product-card:hover {
  background: rgba(25, 25, 25, 0.9) !important;
  border-color: rgba(25, 118, 210, 0.3);
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3) !important;
}

.product-category {
  font-weight: 700;
  letter-spacing: 1px;
}

.font-mono {
  font-family: monospace, Courier, monospace;
}

.border-glow {
  border-color: rgba(255, 255, 255, 0.03) !important;
}

.card-footer {
  background-color: rgba(255, 255, 255, 0.01);
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.border-glass {
  border: 1px solid rgba(255, 255, 255, 0.07) !important;
  box-shadow: 0 24px 50px rgba(0, 0, 0, 0.5) !important;
}

/* ============ SCANNER DIALOG STYLING ============ */
.scanner-container {
  border: 2px solid rgba(255, 255, 255, 0.1);
  background: #000;
  height: 300px;
}

.live-qr-reader {
  width: 100% !important;
  height: 100% !important;
}

.live-qr-reader >>> video {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
}

.scanner-laser {
  position: absolute;
  top: 10%;
  left: 5%;
  width: 90%;
  height: 2px;
  background-color: #ff5252;
  box-shadow: 0 0 8px #ff5252;
  animation: scanning 2s infinite linear;
  pointer-events: none;
  z-index: 5;
}

@keyframes scanning {
  0% {
    top: 10%;
  }
  50% {
    top: 90%;
  }
  100% {
    top: 10%;
  }
}

.table-card {
  background: rgba(18, 18, 18, 0.6) !important;
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.transparent-table {
  background: transparent !important;
}

.transparent-table >>> th {
  font-weight: bold !important;
  color: rgba(255, 255, 255, 0.6) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.transparent-table >>> td {
  border-bottom: 1px solid rgba(255, 255, 255, 0.04) !important;
}

.text-xs {
  font-size: 11px;
}

.tabs-nav >>> .v-tabs-bar {
  background-color: transparent !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}
</style>
